// /tax/$topic — TH detail. Phase P-TAX-DEEP-DIVE.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema, howToSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TaxTopicPage } from "@/components/TaxTopicPage";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/tax/$topic")({
  loader: async ({ params }) => {
    const { getTaxTopic } = await import("@/data/tax-topics");
    const topic = getTaxTopic(params.topic);
    if (!topic) throw notFound();
    return { topic };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const t = loaderData.topic;
    const url = `${SITE}/tax/${t.slug}`;
    const title = `${t.titleTh} — คู่มือเชิงลึก 2026 | NYC`;
    const desc = t.quickAnswerTh.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, "/tax/$topic", "th") }, { name: "description", content: ctaDesc(desc, "/tax/$topic", "th")},
        { property: "og:title", content: ctaTitle(title, "/tax/$topic", "th")}, { property: "og:description", content: ctaDesc(desc, "/tax/$topic", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" }, { property: "og:type", content: "article" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/tax/${t.slug}`, `/en/tax/${t.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", ".quick-answer", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Article", "@id": `${url}#article`,
              headline: t.titleTh, description: desc, inLanguage: "th",
              author: { "@type": "Organization", name: "NYC Legal & Notary Service", url: SITE },
              publisher: { "@type": "Organization", name: "NYC Legal & Notary Service", url: SITE, logo: { "@type": "ImageObject", url: `${SITE}/logo.png` } },
              datePublished: "2026-06-12", dateModified: "2026-06-12",
              mainEntityOfPage: url,
            }),
          },
          howToSchema({
            name: `วิธีปฏิบัติ: ${t.titleTh}`,
            description: t.taglineTh,
            inLanguage: "th",
            steps: t.rulesTh.map((r) => ({ name: r.name, text: r.text })),
          }),
          faqPageSchema(t.faqsTh),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "ภาษีไทย", item: `${SITE}/tax` },
                { "@type": "ListItem", position: 3, name: t.titleTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">ไม่พบหัวข้อนี้</h1>
      <Link to="/tax" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">กลับหน้ารวมภาษี</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">เกิดข้อผิดพลาด</h1>
      <Link to="/tax" className="mt-6 inline-block text-primary">กลับหน้ารวมภาษี</Link>
    </main>
  ),
  component: TaxTopicTh,
});

function TaxTopicTh() {
  const { topic } = Route.useLoaderData();
  return <TaxTopicPage topic={topic} locale="th" hubPath="/tax" relatedBaseTax="/tax" />;
}

